import { createClient } from '@/lib/supabase/client';

const supabase = createClient();
import { Database } from '@/types/database.types';

type EquipmentListing = Database['public']['Tables']['equipment_listings']['Row'];
type InsertEquipmentListing = Database['public']['Tables']['equipment_listings']['Insert'];
type UpdateEquipmentListing = Database['public']['Tables']['equipment_listings']['Update'];
type EquipmentImage = Database['public']['Tables']['equipment_images']['Row'];

export const listingService = {
  async getFeaturedListings(limit: number = 3) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select(`
        id,
        title,
        price,
        location,
        year,
        mileage_hours,
        condition,
        status,
        slug,
        equipment_images(
          image_url,
          alt_text,
          is_primary
        ),
        user_profiles!equipment_listings_seller_id_fkey(
          full_name,
          phone
        )
      `)
      .eq('status', 'approved')
      .eq('is_featured', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Transform data to match expected structure
    const transformedData = data?.map(listing => ({
      ...listing,
      images: listing.equipment_images || [],
      seller: listing.user_profiles || {}
    }));
    
    return transformedData || [];
  },

  async getListings(filters?: {
    status?: string;
    category_id?: string;
    search?: string;
    limit?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) {
    let query = supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(id, image_url, alt_text, is_primary),
        equipment_categories(id, name, slug),
        user_profiles!equipment_listings_seller_id_fkey(id, full_name, email, phone)
      `, { count: 'exact' });

    // Apply status filter
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    // Apply category filter
    if (filters?.category_id) {
      query = query.eq('category_id', filters.category_id);
    }

    // Apply search filter (search in title, manufacturer, model)
    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,manufacturer.ilike.%${filters.search}%,model.ilike.%${filters.search}%`);
    }

    // Apply sorting
    const sortBy = filters?.sort_by || 'created_at';
    const sortOrder = filters?.sort_order || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply limit
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    // Transform data to match expected structure
    const transformedData = data?.map(listing => ({
      ...listing,
      images: listing.equipment_images || [],
      category: listing.equipment_categories,
      seller: listing.user_profiles
    }));

    return {
      listings: transformedData || [],
      total_count: count || 0
    };
  },

  async getListingById(listingId: string) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(id, image_url, alt_text, caption, is_primary, sort_order, file_size, created_at),
        equipment_categories(id, name, slug),
        user_profiles!equipment_listings_seller_id_fkey(id, full_name, email, phone)
      `)
      .eq('id', listingId)
      .single();

    if (error) throw error;

    // Transform data to match expected structure
    if (data) {
      return {
        ...data,
        images: data.equipment_images || [],
        category: data.equipment_categories,
        seller: data.user_profiles
      };
    }

    return null;
  },

  async getAllListings() {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select('id, status, updated_at, created_at')
      .eq('status', 'approved')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async getAllApprovedListings() {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(image_url, alt_text, is_primary),
        equipment_categories(name, slug)
      `)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async getListingBySlug(slug: string) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(image_url, alt_text, caption, is_primary, sort_order),
        equipment_categories(name, slug),
        user_profiles!equipment_listings_seller_id_fkey(full_name, email, phone)
      `)
      .eq('slug', slug)
      .single();
    
    return { data, error };
  },

  async getUserListings(userId: string) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(id, image_url, alt_text, is_primary, file_size, created_at),
        equipment_categories(name)
      `)
      .eq('seller_id', userId)
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async createListing(listing: InsertEquipmentListing) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .insert(listing)
      .select()
      .single();
    
    return { data, error };
  },

  async updateListing(id: string, updates: UpdateEquipmentListing, userId: string) {
    const { data, error } = await supabase
      .from('equipment_listings')
      .update(updates)
      .eq('id', id)
      .eq('seller_id', userId)
      .select()
      .single();
    
    return { data, error };
  },

  async deleteListing(id: string, userId: string) {
    const { error } = await supabase
      .from('equipment_listings')
      .delete()
      .eq('id', id)
      .eq('seller_id', userId);
    
    return { error };
  },

  async searchListings(filters: {
    category?: string;
    listingType?: string;
    condition?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
  }) {
    let query = supabase
      .from('equipment_listings')
      .select(`
        *,
        equipment_images(image_url, is_primary),
        equipment_categories(name, slug)
      `)
      .eq('status', 'approved');
    
    if (filters.category) {
      query = query.eq('category_id', filters.category);
    }
    if (filters.listingType) {
      query = query.eq('listing_type', filters.listingType);
    }
    if (filters.condition) {
      query = query.eq('condition', filters.condition);
    }
    if (filters.minPrice) {
      query = query.gte('price', filters.minPrice);
    }
    if (filters.maxPrice) {
      query = query.lte('price', filters.maxPrice);
    }
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  },

  async getCategories(includeSubcategories: boolean = false) {
    let query = supabase
      .from('equipment_categories')
      .select('*')
      .order('sort_order', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;

    // If includeSubcategories is true, fetch subcategories for each category
    if (includeSubcategories && data) {
      const categoriesWithSubs = await Promise.all(
        data.map(async (category) => {
          const { data: subcategories } = await supabase
            .from('equipment_categories')
            .select('*')
            .eq('parent_id', category.id)
            .order('sort_order', { ascending: true });

          return {
            ...category,
            subcategories: subcategories || []
          };
        })
      );
      return categoriesWithSubs;
    }

    return data || [];
  },

  async incrementViews(listingId: string) {
    const { error } = await supabase.rpc('increment_listing_views', {
      listing_uuid: listingId
    });
    
    return { error };
  },

  async approveListing(listingId: string) {
    const { error } = await supabase
      .from('equipment_listings')
      .update({ 
        status: 'approved',
        approved_at: new Date().toISOString()
      })
      .eq('id', listingId);

    if (error) throw error;
    return { success: true };
  },

  async rejectListing(listingId: string, reason: string) {
    const { error } = await supabase
      .from('equipment_listings')
      .update({ 
        status: 'rejected',
        flagged_reason: reason,
        rejected_at: new Date().toISOString()
      })
      .eq('id', listingId);

    if (error) throw error;
    return { success: true };
  },

  async flagListing(listingId: string, reason: string) {
    const { error } = await supabase
      .from('equipment_listings')
      .update({ 
        status: 'flagged',
        flagged_reason: reason,
        flagged_at: new Date().toISOString()
      })
      .eq('id', listingId);

    if (error) throw error;
    return { success: true };
  },

  async createCategory(categoryData: any) {
    const { data, error } = await supabase
      .from('equipment_categories')
      .insert(categoryData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateCategory(categoryId: string, updates: any) {
    const { data, error } = await supabase
      .from('equipment_categories')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteCategory(categoryId: string) {
    const { error } = await supabase
      .from('equipment_categories')
      .delete()
      .eq('id', categoryId);

    if (error) throw error;
    return { success: true };
  },

  /**
   * Add images to equipment listing
   * @param listingId - UUID of the equipment listing
   * @param images - Array of image data to insert
   */
  async addEquipmentImages(
    listingId: string,
    images: Array<{
      image_url: string;
      alt_text: string;
      caption?: string;
      is_primary?: boolean;
      sort_order?: number;
      file_size?: number;
      image_width?: number;
      image_height?: number;
    }>
  ): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      // If this is the first image and no primary is set, make it primary
      if (images.length > 0 && !images.some(img => img.is_primary)) {
        images[0].is_primary = true;
      }

      const { data, error } = await supabase
        .from('equipment_images')
        .insert(
          images.map((img, index) => ({
            listing_id: listingId,
            image_url: img.image_url,
            alt_text: img.alt_text,
            caption: img.caption,
            is_primary: img.is_primary || false,
            sort_order: img.sort_order !== undefined ? img.sort_order : index,
            file_size: img.file_size,
            image_width: img.image_width,
            image_height: img.image_height
          }))
        )
        .select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Update equipment image metadata
   * @param imageId - UUID of the image to update
   * @param updates - Fields to update
   */
  async updateEquipmentImage(
    imageId: string,
    updates: {
      alt_text?: string;
      caption?: string;
      is_primary?: boolean;
      sort_order?: number;
    }
  ): Promise<{ data: any | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('equipment_images')
        .update(updates)
        .eq('id', imageId)
        .select()
        .single();

      if (error) throw error;

      // If setting as primary, unset other primary images for this listing
      if (updates.is_primary && data) {
        await supabase
          .from('equipment_images')
          .update({ is_primary: false })
          .eq('listing_id', data.listing_id)
          .neq('id', imageId);
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Delete equipment image
   * @param imageId - UUID of the image to delete
   * @param userId - UUID of the user attempting deletion (for authorization)
   */
  async deleteEquipmentImage(
    imageId: string,
    userId: string
  ): Promise<{ error: Error | null }> {
    try {
      // First verify ownership through listing
      const { data: image, error: fetchError } = await supabase
        .from('equipment_images')
        .select(`
          id,
          image_url,
          listing_id,
          is_primary,
          equipment_listings!inner(seller_id)
        `)
        .eq('id', imageId)
        .single();

      if (fetchError) throw fetchError;
      if (!image) throw new Error('Image not found');

      // Check if user owns the listing
      const listing = image.equipment_listings as any;
      if (listing.seller_id !== userId) {
        throw new Error('Unauthorized: You do not own this listing');
      }

      // Extract storage path from URL
      const urlParts = image.image_url.split('/equipment-images/');
      const storagePath = urlParts[1];

      // Delete from database first
      const { error: deleteError } = await supabase
        .from('equipment_images')
        .delete()
        .eq('id', imageId);

      if (deleteError) throw deleteError;

      // Delete from storage (imported storageService at top)
      if (storagePath) {
        await supabase.storage
          .from('equipment-images')
          .remove([storagePath]);
      }

      // If deleted image was primary, set another image as primary
      if (image.is_primary) {
        const { data: remainingImages } = await supabase
          .from('equipment_images')
          .select('id')
          .eq('listing_id', image.listing_id)
          .order('sort_order', { ascending: true })
          .limit(1)
          .single();

        if (remainingImages) {
          await this.updateEquipmentImage(remainingImages.id, { is_primary: true });
        }
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Set primary image for listing
   * @param imageId - UUID of the image to set as primary
   * @param listingId - UUID of the listing
   */
  async setPrimaryImage(
    imageId: string,
    listingId: string
  ): Promise<{ error: Error | null }> {
    try {
      // Unset all primary images for this listing
      await supabase
        .from('equipment_images')
        .update({ is_primary: false })
        .eq('listing_id', listingId);

      // Set new primary image
      const { error } = await supabase
        .from('equipment_images')
        .update({ is_primary: true })
        .eq('id', imageId)
        .eq('listing_id', listingId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Reorder equipment images
   * @param listingId - UUID of the listing
   * @param imageOrder - Array of image IDs in desired order
   */
  async reorderEquipmentImages(
    listingId: string,
    imageOrder: string[]
  ): Promise<{ error: Error | null }> {
    try {
      // Update sort_order for each image
      const updates = imageOrder.map((imageId, index) => ({
        id: imageId,
        sort_order: index
      }));

      for (const update of updates) {
        await supabase
          .from('equipment_images')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id)
          .eq('listing_id', listingId);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  /**
   * Get all images for a listing with details
   * @param listingId - UUID of the listing
   */
  async getEquipmentImages(listingId: string): Promise<{
    data: any[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('equipment_images')
        .select('*')
        .eq('listing_id', listingId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};
function EquipmentListing(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: EquipmentListing is not implemented yet.', args);
  return null;
}

export { EquipmentListing };
function EquipmentCategory(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: EquipmentCategory is not implemented yet.', args);
  return null;
}

export { EquipmentCategory };
function SellerProfile(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: SellerProfile is not implemented yet.', args);
  return null;
}

export { SellerProfile };
function ListingInquiry(...args: any[]): any {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: ListingInquiry is not implemented yet.', args);
  return null;
}

export { ListingInquiry };